<script lang="ts">
import type { ComponentPublicInstance } from 'vue';
import type { SelectMenuFocusHandoffExpose } from '#layers/ui/composables/use-select-menu-focus-handoff';
import type { InputProps, SelectMenuItem, SelectMenuSlots, SelectMenuEmits as USelectMenuEmits, SelectMenuProps as USelectMenuProps } from '#ui/types';
import type { ArrayOrNested, GetItemKeys } from '#ui/types/utils';
import { useFormField } from '#layers/ui/composables/use-form-field';

export interface SelectMenuProps<T extends ArrayOrNested<SelectMenuItem> = ArrayOrNested<SelectMenuItem>, VK extends GetItemKeys<T> | undefined = undefined, M extends boolean = false> extends Omit<USelectMenuProps<T, VK, M>, 'clear'> {
	/**
	 * Show a button (and Backspace shortcut) to clear the value. On clear the
	 * value resets to `null` (or an empty array when `multiple`).
	 */
	clear?: boolean;
	disabled?: boolean;
	readonly?: boolean;
	/** Keep focus with a newly opened control when this menu is dismissed by that interaction. */
	focusHandoff?: boolean;
}

export interface SelectMenuEmits<T extends ArrayOrNested<SelectMenuItem> = ArrayOrNested<SelectMenuItem>, VK extends GetItemKeys<T> | undefined = undefined, M extends boolean = false> extends USelectMenuEmits<T, VK, M> {
	'clear': [];
	'update:modelValue': USelectMenuEmits<T, VK, M>['update:modelValue'];
}
</script>

<script setup lang="ts" generic="T extends ArrayOrNested<SelectMenuItem>, VK extends GetItemKeys<T> | undefined = undefined, M extends boolean = false">
import { useForwardExpose, useForwardPropsEmits } from 'reka-ui';
import { computed, ref } from 'vue';

import { useReadonly } from '#layers/ui/composables/use-readonly';
import { useSelectMenuFocusHandoff } from '#layers/ui/composables/use-select-menu-focus-handoff';
import { mergeUi } from '#layers/ui/utils/merge-ui';
import SelectMenu from '#ui/components/SelectMenu.vue';

defineOptions({ inheritAttrs: false });

const {
	portal = true,
	searchInput = true,
	resetSearchTermOnBlur = true,
	resetSearchTermOnSelect = true,
	resetModelValueOnClear = true,
	highlightOnHover = true,
	focusHandoff = false,
	clear,
	readonly,
	disabled,
	modelValue,
	multiple,
	...forwardedProps
} = defineProps<SelectMenuProps<T, VK, M>>();
const emits = defineEmits<SelectMenuEmits<T, VK, M>>();
const slots = defineSlots<SelectMenuSlots<T, VK, M>>();
const forwarded = useForwardPropsEmits(() => ({
	...forwardedProps,
	portal,
	searchInput,
	resetSearchTermOnBlur,
	resetSearchTermOnSelect,
	resetModelValueOnClear,
	highlightOnHover,
	disabled,
	modelValue,
	multiple,
}), emits);
const { forwardRef } = useForwardExpose();
const selectMenuRef = ref<SelectMenuFocusHandoffExpose | null>(null);
const { onModelUpdate, onOpenUpdate, skipCloseAnimation } = useSelectMenuFocusHandoff({
	enabled: () => focusHandoff,
	selectMenuRef,
});

const { preventKeyboardNavigation, preventPointer } = useReadonly(() => readonly);
const searchTerm = defineModel<string>('searchTerm', { default: '' });

const { emitFormInput, emitFormChange } = useFormField<InputProps>(forwardedProps, { forwardContext: true });

function isValueEmpty(value: unknown) {
	return value == null || value === '' || (Array.isArray(value) && value.length === 0);
}

const isClearShown = computed(
	() => !disabled && !readonly && clear && !isValueEmpty(modelValue),
);

function setSelectMenuRef(value: Element | ComponentPublicInstance | null) {
	forwardRef(value);
	selectMenuRef.value = value as (ComponentPublicInstance & SelectMenuFocusHandoffExpose) | null;
}

function onClearValue() {
	emits('update:modelValue', (multiple ? [] : null) as never);
	emits('clear');
	emitFormChange();
	emitFormInput();
}

function onReadonlyCapture(event: KeyboardEvent) {
	if (preventKeyboardNavigation(event, ['Tab'])) return;

	if (event.key === 'Backspace' && !disabled && isClearShown.value) {
		onClearValue();
	}
}
</script>

<template>
	<SelectMenu
		:ref="setSelectMenuRef"
		v-bind="{ ...forwarded, ...$attrs }"
		v-model:search-term="searchTerm"
		:data-readonly="readonly || undefined"
		:ui="mergeUi(ui, {
			base: isClearShown ? 'pe-7.5' : '',
			content: skipCloseAnimation ? 'data-[state=closed]:animate-none' : '',
			trailing: isClearShown ? 'pe-1' : '',
		})"
		@pointerdown.capture="preventPointer"
		@click.capture="preventPointer"
		@keydown.capture="onReadonlyCapture"
		@update:model-value="onModelUpdate"
		@update:open="onOpenUpdate"
	>
		<template v-for="(_, name) in slots" #[name]="slotData: any">
			<slot :name v-bind="slotData" />
		</template>
		<template v-if="isClearShown" #trailing>
			<DTooltip :text="$t('common.clear')">
				<DButton
					v-if="modelValue"
					data-testid="select-clear"
					icon="lucide:x"
					color="neutral"
					variant="link"
					size="sm"
					@click.stop="onClearValue()"
				/>
			</DTooltip>
		</template>
	</SelectMenu>
</template>
